import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, BrutalCard, Chip } from "../components/brutal";
import { toast } from "sonner";
import { Plus, Save, Check, X, RotateCcw, Trash2, Sparkles, Trophy, Instagram, EyeOff, Pause, Play, UserX, MapPin, Upload, Briefcase, Search, Pencil, AlertTriangle, Loader2, Mail, MailQuestion, Clock, MoreHorizontal, ArrowRight, Camera } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import BulkUploadModal from "../components/BulkUploadModal";

const TABS = [
  { key: "jobs", label: "Jobs" },
  { key: "users", label: "Employees" },
  { key: "stages", label: "Points engine" },
  { key: "bonus", label: "Bonus points" },
  { key: "payouts", label: "Payouts" },
  { key: "spotlight", label: "Spotlight" },
  { key: "danger", label: "Danger zone" },
];

export default function AdminPage() {
  const [tab, setTab] = useState("jobs");
  const [bonusPreselectUser, setBonusPreselectUser] = useState("");

  const openBonusFor = (userId) => {
    setBonusPreselectUser(userId);
    setTab("bonus");
    // scroll to top so the form is visible
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <PageHeader title="Admin Console" subtitle="Manage jobs, users, points, payouts, and spotlights." />
      <div className="brutal p-1 inline-flex gap-1 mb-6 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl font-semibold text-sm whitespace-nowrap ${
              tab === t.key ? "bg-brand-ink text-white" : "hover:bg-neutral-100"
            }`}
            data-testid={`admin-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "jobs" && <JobsAdmin />}
      {tab === "users" && <UsersAdmin onGiveBonus={openBonusFor} />}
      {tab === "stages" && <StagesAdmin />}
      {tab === "bonus" && (
        <BonusPointsAdmin
          preselectUserId={bonusPreselectUser}
          onConsumedPreselect={() => setBonusPreselectUser("")}
        />
      )}
      {tab === "payouts" && <PayoutsAdmin />}
      {tab === "spotlight" && <SpotlightAdmin />}
      {tab === "danger" && <DangerZone />}
    </div>
  );
}

function DangerZone() {
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    const c1 = window.prompt(
      "This will clear ALL stats (points, referrals, shout-outs, spotlights, badges, payouts, notifications) while KEEPING your employees and jobs.\n\nType RESET to continue, or cancel."
    );
    if (c1 !== "RESET") {
      toast("Reset cancelled", { icon: "ℹ️" });
      return;
    }
    if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;

    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-stats?confirm=RESET");
      const totalCleared = Object.values(data.cleared || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `Clean slate! Cleared ${totalCleared} records across ${Object.keys(data.cleared).length} collections. Points reset for ${data.users_points_reset} users.`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border-2 border-red-500 rounded-2xl p-5 bg-red-50" data-testid="danger-zone">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-11 h-11 bg-red-500 border-2 border-black rounded-xl flex items-center justify-center shadow-brutal flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-display font-black text-lg text-red-800">Danger zone</h3>
          <p className="text-sm text-red-900/80 mt-1">
            Wipe all stats for a clean slate — referrals, points, shout-outs, spotlights, badges, payouts, notifications, and activity log. Your employees and jobs stay untouched.
          </p>
        </div>
        <button
          onClick={reset}
          disabled={busy}
          className="btn-brutal border-black bg-red-600 text-white text-sm disabled:opacity-60"
          data-testid="reset-stats-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          {busy ? "Resetting…" : "Reset all stats"}
        </button>
      </div>
    </div>
  );
}

function JobsAdmin() {
  const [open, setOpen] = useState([]);
  const [closed, setClosed] = useState([]);
  const [subTab, setSubTab] = useState("open");
  const [form, setForm] = useState({ title: "", location: "", description: "", requirements: "", status: "open", share_templates: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [summaryJob, setSummaryJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const logoInputRef = React.useRef(null);

  const load = async () => {
    const [o, c] = await Promise.all([
      api.get("/jobs?status=open"),
      api.get("/jobs?status=closed"),
    ]);
    setOpen(o.data);
    setClosed(c.data);
  };
  useEffect(() => { load(); }, []);

  const onLogoChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo must be smaller than 5 MB"); return; }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };
  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    if (logoInputRef.current) logoInputRef.current.value = "";
  };
  const resetForm = () => {
    setEditingId(null);
    setForm({ title: "", location: "", description: "", requirements: "", status: "open", share_templates: "" });
    clearLogo();
  };
  const startEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title: job.title || "",
      location: job.location || "",
      description: job.description || "",
      requirements: job.requirements || "",
      status: job.status || "open",
      share_templates: Array.isArray(job.share_templates) ? job.share_templates.join("\n") : "",
    });
    setLogoFile(null);
    setLogoPreview(job.logo_url || "");
    if (logoInputRef.current) logoInputRef.current.value = "";
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.department_id;
      // share_templates is a newline-separated string in the form; convert to array (or null
      // to fall back to defaults).
      const lines = (form.share_templates || "").split("\n").map((s) => s.trim()).filter(Boolean);
      payload.share_templates = lines.length > 0 ? lines : null;
      let job;
      if (editingId) {
        const { data } = await api.patch(`/jobs/${editingId}`, payload);
        job = data;
      } else {
        const { data } = await api.post("/jobs", payload);
        job = data;
      }
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        try {
          await api.post(`/jobs/${job.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) {
          toast.warning("Saved — but logo upload failed. You can retry.");
        }
      }
      toast.success(editingId ? "Job updated" : "Job created");
      resetForm();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const setStatus = async (job, status) => {
    try {
      await api.patch(`/jobs/${job.id}`, { ...job, status });
      toast.success(status === "open" ? "Job reactivated" : "Job closed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openSummary = async (job) => {
    setSummaryJob(job);
    setSummary(null);
    setSummaryLoading(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/summary`);
      setSummary(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setSummaryJob(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const list = subTab === "open" ? open : closed;

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <BrutalCard className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-xl">Jobs</h3>
          <div className="brutal p-1 inline-flex gap-1" data-testid="jobs-subtabs">
            <button
              onClick={() => setSubTab("open")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${subTab === "open" ? "bg-brand-green" : "hover:bg-neutral-100"}`}
              data-testid="subtab-open"
            >
              Open ({open.length})
            </button>
            <button
              onClick={() => setSubTab("closed")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${subTab === "closed" ? "bg-neutral-300" : "hover:bg-neutral-100"}`}
              data-testid="subtab-closed"
            >
              Closed ({closed.length})
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="border-2 border-dashed border-black rounded-xl p-8 text-center text-neutral-600">
            {subTab === "open" ? "No open jobs yet." : "No closed jobs."}
          </div>
        ) : (
          <>
            <div
              className="space-y-3 overflow-y-auto pr-1 -mr-1"
              style={{ maxHeight: "calc(4 * 92px + 3 * 0.75rem)" }}
              data-testid="admin-jobs-scroll"
            >
              {list.map((j) => (
                <div key={j.id} className="border-2 border-black rounded-xl p-3 flex items-center gap-3" data-testid={`admin-job-${j.id}`}>
                  <div className="w-12 h-12 rounded-lg border-2 border-black overflow-hidden flex items-center justify-center bg-brand-blue text-white flex-shrink-0">
                    {j.logo_url ? (
                      <img src={j.logo_url} alt={j.title} className="w-full h-full object-cover" />
                    ) : (
                      <Briefcase className="w-5 h-5" strokeWidth={2.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{j.title}</div>
                    <div className="text-xs text-neutral-600 truncate">{j.location || "—"}</div>
                  </div>
                  <Chip color={j.status === "open" ? "bg-brand-green" : "bg-neutral-200"}>{j.status}</Chip>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => startEdit(j)}
                      className="btn-brutal btn-white text-xs"
                      data-testid={`edit-job-${j.id}`}
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => openSummary(j)}
                      className="btn-brutal btn-white text-xs"
                      data-testid={`summary-job-${j.id}`}
                      title="View close-out summary"
                    >
                      <Sparkles className="w-3 h-3" />
                    </button>
                    {j.status === "open" ? (
                      <button
                        onClick={() => setStatus(j, "closed")}
                        className="btn-brutal btn-white text-xs"
                        data-testid={`close-job-${j.id}`}
                      >
                        Close
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(j, "open")}
                        className="btn-brutal btn-green text-xs"
                        data-testid={`reactivate-job-${j.id}`}
                      >
                        <RotateCcw className="w-3 h-3" /> Reactivate
                      </button>
                    )}
                    <button
                      onClick={() => remove(j)}
                      className="btn-brutal bg-red-500 text-white text-xs"
                      data-testid={`delete-job-${j.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {list.length > 4 && (
              <div className="text-[11px] text-neutral-500 mt-2 text-center">
                Showing all {list.length} — scroll to see more
              </div>
            )}
          </>
        )}
      </BrutalCard>

      <BrutalCard className="p-5">
        <h3 className="font-display font-black text-xl mb-1">{editingId ? "Edit job" : "Create job"}</h3>
        {editingId && (
          <p className="text-[11px] text-neutral-600 mb-3">Editing — clear changes with Cancel.</p>
        )}
        <div className="space-y-3">
          <input
            className="input-brutal"
            placeholder="Title (e.g. Senior Backend Engineer)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="new-job-title"
          />
          <input
            className="input-brutal"
            placeholder="Location (e.g. Remote, London, Hybrid NYC)"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="new-job-location"
          />
          <textarea
            className="input-brutal resize-none"
            placeholder="Description"
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="new-job-description"
          />
          <textarea
            className="input-brutal resize-none"
            placeholder="Requirements (optional)"
            rows={4}
            value={form.requirements}
            onChange={(e) => setForm({ ...form, requirements: e.target.value })}
            data-testid="new-job-requirements"
          />

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">
              Share templates <span className="text-neutral-500 font-normal normal-case tracking-normal">(one per line)</span>
            </label>
            <textarea
              className="input-brutal resize-none font-mono text-xs leading-relaxed"
              rows={4}
              value={form.share_templates}
              onChange={(e) => setForm({ ...form, share_templates: e.target.value })}
              placeholder={"🚨 Urgent need — {title}{location_clause}. If you know someone right, send them my way:\n💛 Heart-led role: {title}{location_clause}. Could be perfect for someone you know:\n✨ Career changer? We're hiring {title}{location_clause}. Think it could be a good fit?"}
              data-testid="new-job-share-templates"
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Each line is a tone employees can pick when sharing this job. Use <code className="bg-neutral-100 px-1 rounded">{`{title}`}</code> and <code className="bg-neutral-100 px-1 rounded">{`{location_clause}`}</code> to auto-fill. Leave blank to use the platform defaults.
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest mb-1">Company logo <span className="text-neutral-500 font-normal normal-case tracking-normal">(optional)</span></label>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl border-2 border-black overflow-hidden flex items-center justify-center bg-brand-blue text-white flex-shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo preview" className="w-full h-full object-cover" />
                ) : (
                  <Briefcase className="w-5 h-5" strokeWidth={2.5} />
                )}
              </div>
              <label className="btn-brutal btn-white text-xs cursor-pointer flex-1 justify-center">
                <Upload className="w-3.5 h-3.5" />
                {logoFile ? logoFile.name.length > 18 ? logoFile.name.slice(0, 18) + "…" : logoFile.name : "Choose image"}
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onLogoChange} data-testid="new-job-logo" />
              </label>
              {logoFile && (
                <button onClick={clearLogo} className="btn-brutal bg-red-500 text-white text-xs" title="Remove logo">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">PNG/JPG/WebP/GIF · max 5MB</div>
          </div>

          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-brutal btn-primary flex-1" data-testid="create-job">
              {editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {busy ? "Saving…" : editingId ? "Save changes" : "Create"}
            </button>
            {editingId && (
              <button onClick={resetForm} className="btn-brutal btn-white" data-testid="cancel-edit-job">
                Cancel
              </button>
            )}
          </div>
        </div>
      </BrutalCard>

      {summaryJob && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSummaryJob(null)}
          data-testid="job-summary-modal"
        >
          <div
            className="bg-white border-2 border-black rounded-2xl shadow-brutal max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b-2 border-black sticky top-0 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Close-out summary</div>
                <h3 className="font-display font-black text-2xl leading-tight" data-testid="summary-title">{summaryJob.title}</h3>
                <div className="text-sm text-neutral-600">{summaryJob.location || "—"} · <Chip color={summaryJob.status === "open" ? "bg-brand-green" : "bg-neutral-200"}>{summaryJob.status}</Chip></div>
              </div>
              <button
                onClick={() => setSummaryJob(null)}
                className="w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center flex-shrink-0"
                data-testid="summary-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {summaryLoading && (
                <div className="flex items-center gap-2 text-sm text-neutral-600" data-testid="summary-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Crunching the numbers…
                </div>
              )}
              {summary && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <SummaryStat label="Shares" value={summary.shares} color="bg-brand-yellow" testid="summary-shares" />
                    <SummaryStat label="Referrals" value={summary.referrals} color="bg-white" testid="summary-referrals" />
                    <SummaryStat label="Self-applies" value={summary.self_applies} color="bg-white" testid="summary-self-applies" />
                    <SummaryStat label="Hires" value={summary.hires} color="bg-brand-green" testid="summary-hires" />
                    <SummaryStat label="Points paid" value={summary.points_paid} color="bg-white" testid="summary-points" />
                    <SummaryStat label="Saved vs. agencies" value={`€${summary.cost_saved_eur.toLocaleString()}`} color="bg-brand-orange text-white" testid="summary-saved" />
                  </div>

                  <div className="border-2 border-black rounded-xl p-4 bg-neutral-50">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Funnel</div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-display font-black text-2xl" data-testid="summary-conv-share-apply">{summary.conversion_share_to_apply}%</div>
                        <div className="text-xs text-neutral-600">Share → Referral</div>
                      </div>
                      <div>
                        <div className="font-display font-black text-2xl" data-testid="summary-conv-apply-hire">{summary.conversion_apply_to_hire}%</div>
                        <div className="text-xs text-neutral-600">Referral → Hire</div>
                      </div>
                    </div>
                  </div>

                  {summary.by_platform?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Shares by platform</div>
                      <div className="flex flex-wrap gap-2" data-testid="summary-platforms">
                        {summary.by_platform.map((p) => (
                          <span key={p.platform} className="inline-flex items-center gap-1 border-2 border-black rounded-full px-3 py-1 text-xs font-semibold bg-white">
                            {p.platform || "unknown"}
                            <span className="bg-brand-ink text-white rounded-full px-1.5 text-[10px]">{p.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, color, testid }) {
  return (
    <div className={`border-2 border-black rounded-xl p-3 ${color}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display font-black text-2xl leading-tight">{value}</div>
    </div>
  );
}

function UsersAdmin({ onGiveBonus }) {
  // Short relative-time formatter for the pending-since timestamp in the Account column.
  const formatRelative = (iso) => {
    if (!iso) return "";
    const now = Date.now();
    const then = new Date(iso).getTime();
    if (!then) return "";
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  };
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [query, setQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState("all"); // all | activated | pending
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "employee", team: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited in the details modal
  const [editForm, setEditForm] = useState({ name: "", email: "", display_name: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [displayAvatarBusy, setDisplayAvatarBusy] = useState(false);
  const displayAvatarInputRef = React.useRef(null);
  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      display_name: u.display_name || "",
    });
  };
  const uploadDisplayAvatar = async (file) => {
    if (!editing || !file) return;
    setDisplayAvatarBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/users/${editing.id}/display-avatar`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing(data);
      setUsers((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      toast.success("Display avatar updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDisplayAvatarBusy(false);
      if (displayAvatarInputRef.current) displayAvatarInputRef.current.value = "";
    }
  };
  const removeDisplayAvatar = async () => {
    if (!editing) return;
    setDisplayAvatarBusy(true);
    try {
      const { data } = await api.delete(`/users/${editing.id}/display-avatar`);
      setEditing(data);
      setUsers((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data, display_avatar_url: undefined } : x)));
      toast.success("Display avatar removed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDisplayAvatarBusy(false);
    }
  };
  const saveEdit = async () => {
    if (!editing) return;
    setEditBusy(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        display_name: editing.role === "admin" ? editForm.display_name : null,
      };
      await api.patch(`/users/${editing.id}/details`, payload);
      toast.success("User details updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditBusy(false);
    }
  };
  const load = (withRemoved = showRemoved) =>
    api.get("/users", { params: withRemoved ? { include_removed: true } : {} }).then((r) => setUsers(r.data));
  useEffect(() => { load(showRemoved); api.get("/teams").then((r) => setTeams(r.data)); }, [showRemoved]);
  const create = async () => {
    try {
      const payload = { email: form.email, password: form.password, name: form.name, role: form.role, office: form.team || null };
      // Strip empty password so backend auto-generates one
      if (!payload.password) delete payload.password;
      const { data } = await api.post("/users", payload);
      if (data?.welcome_email_sent === false) {
        toast.warning(`${data.name} created — but the welcome email failed to send. They can use “Forgot password?” on the login page.`);
      } else {
        toast.success(`${data.name} created — welcome email sent to ${data.email}`);
      }
      setForm({ email: "", password: "", name: "", role: "employee", team: "" });
      setAddOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };
  const changeStatus = async (u, status, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/users/${u.id}/status`, { status });
      const label = { active: "reactivated", paused: "paused", removed: "removed" }[status];
      toast.success(`${u.name} ${label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const hardDelete = async (u) => {
    const typed = window.prompt(
      `Permanently delete ${u.name}?\n\nThis wipes the user AND all their referrals, points, payouts, shout-outs, badges and notifications. This CANNOT be undone.\n\nType DELETE to confirm.`
    );
    if (typed !== "DELETE") return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} permanently deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const resendWelcome = async (u) => {
    const verb = u.activated ? "password-reset" : "welcome";
    if (!window.confirm(`Send a fresh ${verb} email to ${u.email}? Any previous setup link will stop working.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/resend-welcome`);
      if (data?.email_sent) {
        toast.success(`New ${verb} email sent to ${data.email}`);
      } else {
        toast.warning(`Link re-issued but email send failed for ${data?.email || u.email}. Share the link manually or retry.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const clearUserStats = async (u) => {
    if (!window.confirm(`Clear stats for ${u.name}? This wipes their points, referrals, badges, shout-outs received, payouts, notifications, spotlight history, and shares — but keeps the account, email, password, avatar and privacy settings intact. This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/clear-stats`);
      const totals = data?.cleared || {};
      const sum = Object.values(totals).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Stats cleared for ${u.name} — ${sum} record${sum === 1 ? "" : "s"} wiped, points reset to 0`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const [nudging, setNudging] = useState(false);
  const nudgeAllPending = async () => {
    const count = users.filter((u) => !u.activated && u.status !== "removed").length;
    if (count === 0) {
      toast("No pending employees — everyone's activated!", { icon: "✅" });
      return;
    }
    if (!window.confirm(`Send a fresh welcome email to all ${count} pending employee${count === 1 ? "" : "s"}? This will invalidate their previous setup links.`)) return;
    setNudging(true);
    try {
      const { data } = await api.post("/users/nudge-pending");
      const failed = data.emails_failed || 0;
      if (failed > 0) {
        toast.warning(`Nudged ${data.emails_sent} of ${data.pending_count} — ${failed} email${failed === 1 ? "" : "s"} failed.`);
      } else {
        toast.success(`Nudged ${data.emails_sent} pending employee${data.emails_sent === 1 ? "" : "s"} — fresh welcome emails sent.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNudging(false);
    }
  };
  const onStatusSelect = (u, value) => {
    if (value === u.status) return;
    if (value === "active") return changeStatus(u, "active", u.status === "removed" ? `Restore ${u.name} to active?` : null);
    if (value === "paused") return changeStatus(u, "paused", `Pause ${u.name}? They won't be able to log in until you reactivate.`);
    if (value === "removed") return changeStatus(u, "removed", `Remove ${u.name}? Their history stays but they won't appear in any lists.`);
    if (value === "delete") return hardDelete(u);
  };
  const optOutChips = (u) => {
    const perms = u.permissions || {};
    const chips = [];
    if (perms.show_on_leaderboard === false)
      chips.push({ key: "lb", icon: Trophy, label: "No leaderboard" });
    if (perms.allow_spotlight_share === false)
      chips.push({ key: "sp", icon: Instagram, label: "No spotlight" });
    if (perms.allow_badge_share === false)
      chips.push({ key: "bg", icon: Sparkles, label: "No badge share" });
    return chips;
  };
  const statusChip = (status) => {
    if (status === "paused") return { cls: "bg-brand-yellow", label: "Paused" };
    if (status === "removed") return { cls: "bg-neutral-300 text-neutral-600", label: "Removed" };
    return { cls: "bg-brand-green", label: "Active" };
  };

  const q = query.trim().toLowerCase();
  const baseFiltered = q
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.team || u.office || "").toLowerCase().includes(q)
      )
    : users;
  const filteredUsers = accountFilter === "all"
    ? baseFiltered
    : baseFiltered.filter((u) => (accountFilter === "activated" ? !!u.activated : !u.activated));

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedUsers = filteredUsers.slice(pageStart, pageStart + PAGE_SIZE);

  // Reset to page 1 whenever filter / search changes
  useEffect(() => { setPage(1); }, [query, accountFilter, showRemoved]);

  return (
    <div>
      <BrutalCard className="p-5">
        <div className="sticky top-0 z-20 bg-white -mx-5 -mt-5 px-5 pt-5 pb-3 border-b-2 border-black/10 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="font-display font-black text-xl">Employees ({users.length})</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setAddOpen(true)}
              className="btn-brutal btn-primary text-xs"
              data-testid="open-add-employee"
              title="Add a single employee"
            >
              <Plus className="w-3.5 h-3.5" /> Add employee
            </button>
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              className="btn-brutal btn-yellow text-xs"
              data-testid="open-bulk-upload"
              title="Bulk upload via CSV or Excel"
            >
              <Upload className="w-3.5 h-3.5" /> Bulk upload
            </button>
            {users.some((u) => !u.activated && u.status !== "removed") && (
              <button
                onClick={nudgeAllPending}
                disabled={nudging}
                className="btn-brutal bg-brand-blue text-white border-black text-xs disabled:opacity-60"
                title="Send a fresh welcome email to every employee who hasn't activated yet"
                data-testid="nudge-all-pending-btn"
              >
                {nudging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailQuestion className="w-3.5 h-3.5" />}
                Nudge pending ({users.filter((u) => !u.activated && u.status !== "removed").length})
              </button>
            )}
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider cursor-pointer">
              <input
                type="checkbox"
                checked={showRemoved}
                onChange={(e) => setShowRemoved(e.target.checked)}
                data-testid="show-removed-toggle"
                className="w-4 h-4 border-2 border-black"
              />
              Show removed
            </label>
          </div>
        </div>

        <div className="flex items-center gap-2 border-2 border-black rounded-xl px-3 py-2 bg-white">
          <Search className="w-4 h-4" />
          <input
            placeholder="Search by name, email, or team…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 outline-none bg-transparent text-sm"
            data-testid="employees-search"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-neutral-500 hover:text-black" title="Clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="employees-filter-pills">
          <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Filter:</span>
          {[
            { key: "all", label: "All", count: baseFiltered.length },
            { key: "activated", label: "Activated", count: baseFiltered.filter((u) => !!u.activated).length },
            { key: "pending", label: "Pending", count: baseFiltered.filter((u) => !u.activated).length },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setAccountFilter(f.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 border-black transition-colors ${
                accountFilter === f.key
                  ? f.key === "activated"
                    ? "bg-brand-green"
                    : f.key === "pending"
                    ? "bg-brand-yellow"
                    : "bg-brand-ink text-white"
                  : "bg-white hover:bg-neutral-100"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label} <span className="opacity-70 ml-1">({f.count})</span>
            </button>
          ))}
        </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="border-2 border-dashed border-black rounded-xl p-8 text-center text-neutral-600 text-sm">
            {q ? `No employees match "${query}".` : "No employees yet."}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto" data-testid="employees-table-wrap">
              <table className="w-full text-sm">
                <thead className="bg-brand-ink text-white">
                  <tr>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Role</th>
                    <th className="p-2 text-left">Team</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Account</th>
                    <th className="p-2 text-left">Privacy</th>
                    <th className="p-2 text-right">Pts</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((u) => {
                const chips = optOutChips(u);
                const status = u.status || "active";
                const sc = statusChip(status);
                const isRemoved = status === "removed";
                return (
                  <tr key={u.id} className={`border-t-2 border-black/10 align-top ${isRemoved ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <img
                          src={u.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(u.name)}`}
                          alt=""
                          className="w-8 h-8 rounded-lg border-2 border-black object-cover bg-white"
                        />
                        <span className="font-semibold">{u.name}</span>
                      </div>
                    </td>
                    <td className="p-2 text-xs">{u.email}</td>
                    <td className="p-2">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={isRemoved}
                        className={`input-brutal py-1 px-2 text-xs font-bold ${u.role === "admin" ? "bg-brand-orange text-white" : "bg-brand-yellow"}`}
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="employee">employee</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td className="p-2">{u.team || u.office || "—"}</td>
                    <td className="p-2">
                      <select
                        value={status}
                        onChange={(e) => onStatusSelect(u, e.target.value)}
                        className={`input-brutal py-1 pl-2 pr-6 text-[10px] font-bold uppercase tracking-wider min-w-[96px] ${sc.cls}`}
                        data-testid={`status-select-${u.id}`}
                        title="Change status"
                      >
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="removed">Removed</option>
                        {isRemoved && <option value="delete">Delete forever…</option>}
                      </select>
                    </td>
                    <td className="p-2">
                      {u.activated ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border-2 border-black rounded-full bg-brand-green text-black"
                          title="This employee has set their password and logged in"
                          data-testid={`account-activated-${u.id}`}
                        >
                          <Check className="w-3 h-3" strokeWidth={2.8} /> Activated
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1" data-testid={`account-pending-${u.id}`}>
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border-2 border-black rounded-full bg-brand-yellow text-black self-start"
                            title="This employee hasn't set their password yet"
                          >
                            <Clock className="w-3 h-3" strokeWidth={2.8} /> Pending
                          </span>
                          {u.created_at && (
                            <span
                              className="text-[10px] text-neutral-600 font-semibold"
                              title={`Created ${new Date(u.created_at).toLocaleString()}`}
                              data-testid={`pending-since-${u.id}`}
                            >
                              Created {formatRelative(u.created_at)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      {chips.length === 0 ? (
                        <span className="text-[11px] text-neutral-500 font-semibold">All public</span>
                      ) : (
                        <div className="flex flex-wrap gap-1" data-testid={`privacy-chips-${u.id}`}>
                          {chips.map((c) => {
                            const Icon = c.icon;
                            return (
                              <span
                                key={c.key}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border-2 border-black rounded-md bg-neutral-200 text-neutral-800"
                                title={c.label}
                                data-testid={`privacy-chip-${u.id}-${c.key}`}
                              >
                                <EyeOff className="w-3 h-3" />
                                <Icon className="w-3 h-3" />
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right font-bold">{u.total_points || 0}</td>
                    <td className="p-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="btn-brutal btn-white text-xs w-9 h-9 p-0 justify-center"
                            data-testid={`row-actions-${u.id}`}
                            title="Actions"
                            aria-label={`Actions for ${u.name}`}
                          >
                            <MoreHorizontal className="w-4 h-4" strokeWidth={2.6} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="border-2 border-black rounded-xl shadow-brutal bg-white min-w-[200px]">
                          <DropdownMenuLabel className="font-display font-black text-xs uppercase tracking-widest text-neutral-500">
                            {u.name}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {u.role === "employee" && !isRemoved && onGiveBonus && (
                            <DropdownMenuItem
                              onClick={() => onGiveBonus(u.id)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`give-bonus-${u.id}`}
                            >
                              <Sparkles className="w-4 h-4 text-brand-yellow" strokeWidth={2.4} /> Give bonus points
                            </DropdownMenuItem>
                          )}
                          {!isRemoved && (
                            <DropdownMenuItem
                              onClick={() => resendWelcome(u)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`resend-welcome-${u.id}`}
                            >
                              {u.activated ? <Mail className="w-4 h-4" /> : <MailQuestion className="w-4 h-4 text-brand-blue" />}
                              {u.activated ? "Send password reset" : "Resend welcome email"}
                            </DropdownMenuItem>
                          )}
                          {!isRemoved && (
                            <DropdownMenuItem
                              onClick={() => openEdit(u)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil className="w-4 h-4" /> Edit details
                            </DropdownMenuItem>
                          )}
                          {!isRemoved && (
                            <DropdownMenuItem
                              onClick={() => clearUserStats(u)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`clear-stats-${u.id}`}
                            >
                              <RotateCcw className="w-4 h-4 text-brand-orange" /> Clear stats
                            </DropdownMenuItem>
                          )}
                          {status === "active" && (
                            <DropdownMenuItem
                              onClick={() => changeStatus(u, "paused", `Pause ${u.name}? They won't be able to log in until you reactivate.`)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`pause-${u.id}`}
                            >
                              <Pause className="w-4 h-4" /> Pause employee
                            </DropdownMenuItem>
                          )}
                          {status === "paused" && (
                            <DropdownMenuItem
                              onClick={() => changeStatus(u, "active")}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`resume-${u.id}`}
                            >
                              <Play className="w-4 h-4 text-brand-green" /> Reactivate employee
                            </DropdownMenuItem>
                          )}
                          {isRemoved && (
                            <DropdownMenuItem
                              onClick={() => changeStatus(u, "active", `Restore ${u.name} to active?`)}
                              className="gap-2 cursor-pointer font-semibold"
                              data-testid={`restore-${u.id}`}
                            >
                              <RotateCcw className="w-4 h-4 text-brand-green" /> Restore
                            </DropdownMenuItem>
                          )}
                          {(status !== "removed" || isRemoved) && <DropdownMenuSeparator />}
                          {status !== "removed" && (
                            <DropdownMenuItem
                              onClick={() => changeStatus(u, "removed", `Remove ${u.name}? Their history stays but they won't appear in any lists.`)}
                              className="gap-2 cursor-pointer font-semibold text-red-700 focus:text-red-700"
                              data-testid={`remove-${u.id}`}
                            >
                              <UserX className="w-4 h-4" /> Remove employee
                            </DropdownMenuItem>
                          )}
                          {isRemoved && (
                            <DropdownMenuItem
                              onClick={() => hardDelete(u)}
                              className="gap-2 cursor-pointer font-semibold text-red-700 focus:text-red-700"
                              data-testid={`hard-delete-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" /> Delete forever
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
                </tbody>
              </table>
            </div>
            {filteredUsers.length > PAGE_SIZE ? (
              <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid="employees-pagination">
                <div className="text-xs text-neutral-600">
                  Showing <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageStart + PAGE_SIZE, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong>
                  {q ? <> matching <em>"{query}"</em></> : null}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={safePage === 1}
                    className="btn-brutal btn-white text-xs disabled:opacity-40"
                    data-testid="pagination-first"
                    aria-label="First page"
                  >
                    «
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="btn-brutal btn-white text-xs disabled:opacity-40"
                    data-testid="pagination-prev"
                    aria-label="Previous page"
                  >
                    ‹ Prev
                  </button>
                  <span className="text-xs font-bold px-3 py-1.5 border-2 border-black rounded-xl bg-brand-yellow" data-testid="pagination-current">
                    {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="btn-brutal btn-white text-xs disabled:opacity-40"
                    data-testid="pagination-next"
                    aria-label="Next page"
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={safePage === totalPages}
                    className="btn-brutal btn-white text-xs disabled:opacity-40"
                    data-testid="pagination-last"
                    aria-label="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-neutral-500 mt-2 text-center" data-testid="employees-count-summary">
                {filteredUsers.length} {filteredUsers.length === 1 ? "employee" : "employees"}{q ? ` matching "${query}"` : ""}
              </div>
            )}
          </>
        )}
      </BrutalCard>

      {/* Edit-details modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={() => !editBusy && setEditing(null)}
          data-testid="edit-user-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-t-2 sm:border-2 border-black sm:rounded-2xl rounded-t-2xl shadow-brutal w-full sm:max-w-md max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300"
          >
            <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-black text-xl leading-tight">Edit user</h3>
                <div className="text-xs text-neutral-600 truncate">{editing.email}</div>
              </div>
              <button
                onClick={() => !editBusy && setEditing(null)}
                className="w-8 h-8 border-2 border-black rounded-xl flex items-center justify-center"
                data-testid="edit-modal-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Name</span>
                <input
                  className="input-brutal mt-1"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="edit-name-input"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Email</span>
                <input
                  className="input-brutal mt-1"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="name@company.ie"
                  data-testid="edit-email-input"
                />
                <span className="text-[11px] text-neutral-500">Used for login. They'll need to use the new email next time they sign in.</span>
              </label>
              {editing.role === "admin" && (
                <>
                  <div className="border-t-2 border-dashed border-neutral-300 pt-3 mt-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Public display avatar</div>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-2xl border-2 border-black bg-neutral-100 overflow-hidden flex items-center justify-center" data-testid="display-avatar-preview">
                        {editing.display_avatar_url ? (
                          <img
                            src={editing.display_avatar_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${editing.display_avatar_url}` : editing.display_avatar_url}
                            alt=""
                            className="w-full h-full object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <Camera className="w-5 h-5 text-neutral-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          ref={displayAvatarInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadDisplayAvatar(e.target.files[0])}
                          data-testid="display-avatar-input"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => displayAvatarInputRef.current?.click()}
                            disabled={displayAvatarBusy}
                            className="btn-brutal btn-white text-xs disabled:opacity-50"
                            data-testid="display-avatar-upload-btn"
                          >
                            {displayAvatarBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {editing.display_avatar_url ? "Replace" : "Upload"}
                          </button>
                          {editing.display_avatar_url && (
                            <button
                              type="button"
                              onClick={removeDisplayAvatar}
                              disabled={displayAvatarBusy}
                              className="btn-brutal text-xs bg-red-500 text-white border-black disabled:opacity-50"
                              data-testid="display-avatar-remove-btn"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Remove
                            </button>
                          )}
                        </div>
                        <span className="text-[11px] text-neutral-500 block mt-1">Brand logo shown next to public posts. Max 5 MB. PNG / JPG / WebP / GIF.</span>
                      </div>
                    </div>
                  </div>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Public display name</span>
                    <input
                      className="input-brutal mt-1"
                      value={editForm.display_name}
                      onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                      placeholder="e.g. Bluebird Care Kerry & West Cork"
                      data-testid="edit-display-name-input"
                    />
                    <span className="text-[11px] text-neutral-500">Shown as the byline on shout-outs instead of the personal name. Leave blank to use the name above.</span>
                  </label>
                </>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t-2 border-black px-5 py-4 flex justify-end gap-2">
              <button
                onClick={() => !editBusy && setEditing(null)}
                className="btn-brutal btn-white text-sm"
                data-testid="edit-cancel-btn"
                disabled={editBusy}
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={editBusy}
                className="btn-brutal btn-primary text-sm disabled:opacity-50"
                data-testid="edit-save-btn"
              >
                {editBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editBusy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add-employee modal */}
      {addOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={() => setAddOpen(false)}
          data-testid="add-employee-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-t-2 sm:border-2 border-black sm:rounded-2xl rounded-t-2xl shadow-brutal w-full sm:max-w-md max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300"
          >
            <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Add an employee</h3>
              <button
                onClick={() => setAddOpen(false)}
                className="w-9 h-9 border-2 border-black rounded-xl bg-white hover:bg-neutral-100 flex items-center justify-center"
                data-testid="add-employee-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
            <div className="px-5 py-5 space-y-3">
              <input className="input-brutal" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              <input className="input-brutal" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              <input className="input-brutal" type="password" placeholder="Temporary password (optional — leave blank)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              <select className="input-brutal" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
              <select className="input-brutal" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="new-user-team">
                <option value="">Select team</option>
                {teams.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
              <button onClick={create} className="btn-brutal btn-primary w-full" data-testid="create-user">
                <Plus className="w-4 h-4" /> Create employee
              </button>
              <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
                New employees get a <strong>welcome email</strong> with a one-time setup link (valid 7 days). They'll set their own password — leave the temp password field blank unless you have a reason not to.
              </p>
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={load} />
    </div>
  );
}

function StagesAdmin() {
  const [stages, setStages] = useState([]);
  const load = () => api.get("/stages").then((r) => setStages(r.data));
  useEffect(() => { load(); }, []);
  const save = async (s) => {
    try {
      await api.patch(`/stages/${s.id}`, { points: s.points });
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <BrutalCard className="p-5">
      <h3 className="font-display font-black text-xl mb-4">Points engine</h3>
      <p className="text-sm text-neutral-700 mb-4">Adjust how many points each stage awards. Past transactions are not modified.</p>
      <div className="space-y-3">
        {stages.map((s, i) => (
          <div key={s.id} className="grid md:grid-cols-[1fr_120px_120px] gap-3 items-center border-2 border-black rounded-xl p-3">
            <div>
              <div className="font-semibold">{s.name}</div>
              <div className="text-xs text-neutral-600">Order #{s.order}</div>
            </div>
            <input
              type="number"
              className="input-brutal"
              value={s.points}
              onChange={(e) => {
                const v = parseInt(e.target.value) || 0;
                setStages((arr) => arr.map((x, idx) => idx === i ? { ...x, points: v } : x));
              }}
              data-testid={`stage-points-${s.name.replace(/\s+/g, "-")}`}
            />
            <button onClick={() => save(s)} className="btn-brutal btn-primary" data-testid={`save-stage-${s.name.replace(/\s+/g, "-")}`}>
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        ))}
      </div>
    </BrutalCard>
  );
}

function BonusPointsAdmin({ preselectUserId, onConsumedPreselect }) {
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ user_id: "", points: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [u, h] = await Promise.all([api.get("/users"), api.get("/bonus-points")]);
    setUsers(u.data.filter((x) => x.role === "employee"));
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  // React to preselect from Users tab
  useEffect(() => {
    if (preselectUserId) {
      setForm((f) => ({ ...f, user_id: preselectUserId }));
      if (onConsumedPreselect) onConsumedPreselect();
    }
    // eslint-disable-next-line
  }, [preselectUserId]);

  const award = async (e) => {
    e.preventDefault();
    const pts = parseInt(form.points);
    if (!form.user_id) { toast.error("Pick an employee"); return; }
    if (!pts || pts === 0) { toast.error("Points must be non-zero"); return; }
    if (!form.reason.trim()) { toast.error("A reason is required for the audit trail"); return; }
    setBusy(true);
    try {
      await api.post("/bonus-points", { user_id: form.user_id, points: pts, reason: form.reason });
      const target = users.find((u) => u.id === form.user_id);
      toast.success(`${pts > 0 ? "+" : ""}${pts} points awarded to ${target?.name || "employee"}`);
      setForm({ user_id: "", points: "", reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const presets = [10, 25, 50, 100];

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={award} className="lg:col-span-2">
        <BrutalCard className="p-5" data-testid="bonus-form">
          <h3 className="font-display font-black text-xl mb-1">Allocate bonus points</h3>
          <p className="text-sm text-neutral-700 mb-5">
            Give ad-hoc points for anything — referral-related or not. Use a negative amount to deduct.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Employee</label>
              <select
                className="input-brutal"
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                data-testid="bonus-user"
              >
                <option value="">Pick an employee…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} — {u.team || u.office || "—"} · {u.total_points || 0} pts
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Points</label>
              <input
                type="number"
                className="input-brutal"
                value={form.points}
                onChange={(e) => setForm({ ...form, points: e.target.value })}
                placeholder="e.g. 25 or -10"
                data-testid="bonus-points-input"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setForm({ ...form, points: String(p) })}
                    className="chip bg-white hover:bg-brand-yellow"
                    data-testid={`bonus-preset-${p}`}
                  >
                    +{p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, points: String(-10) })}
                  className="chip bg-red-200"
                  data-testid="bonus-preset-neg"
                >
                  −10 (deduct)
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Reason</label>
              <textarea
                rows={3}
                className="input-brutal resize-none"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Helped close Q1 role outside the referral pipeline"
                data-testid="bonus-reason"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-brutal btn-primary w-full py-3"
              data-testid="bonus-submit"
            >
              <Sparkles className="w-4 h-4" /> {busy ? "Allocating…" : "Allocate points"}
            </button>
          </div>
        </BrutalCard>
      </form>

      <BrutalCard className="p-5 bg-brand-yellow">
        <h3 className="font-display font-black text-xl">Tips</h3>
        <ul className="mt-3 text-sm space-y-2 list-disc list-inside">
          <li>Use for informal shout-outs, extra effort, or one-off corrections.</li>
          <li>Negative amounts reverse mistakes — all changes are audit-logged.</li>
          <li>Employees get an instant notification.</li>
          <li>History appears on their dashboard activity.</li>
        </ul>
      </BrutalCard>

      <div className="lg:col-span-3">
        <h3 className="font-display font-black text-xl mb-3">History</h3>
        {history.length === 0 ? (
          <BrutalCard className="p-6 text-center text-neutral-600">No bonus allocations yet.</BrutalCard>
        ) : (
          <BrutalCard className="p-0 overflow-hidden">
            <table className="w-full text-sm" data-testid="bonus-history">
              <thead className="bg-brand-ink text-white">
                <tr>
                  <th className="p-3 text-left">Employee</th>
                  <th className="p-3 text-left">Points</th>
                  <th className="p-3 text-left">Reason</th>
                  <th className="p-3 text-left">Awarded by</th>
                  <th className="p-3 text-left">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t-2 border-black/10" data-testid={`bonus-row-${h.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {h.user_avatar && <img src={h.user_avatar} alt="" className="w-7 h-7 rounded-full border-2 border-black object-cover" />}
                        <span className="font-semibold">{h.user_name || "—"}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`chip ${h.points > 0 ? "bg-brand-green" : "bg-red-200"}`}>
                        {h.points > 0 ? `+${h.points}` : h.points} pts
                      </span>
                    </td>
                    <td className="p-3 text-neutral-700">{(h.reason || "").replace(/^Bonus:\s*/i, "")}</td>
                    <td className="p-3 text-xs text-neutral-700">{h.awarded_by_name || "—"}</td>
                    <td className="p-3 text-xs text-neutral-700">{new Date(h.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </BrutalCard>
        )}
      </div>
    </div>
  );
}

function PayoutsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/payouts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const act = async (r, action) => {
    try {
      await api.patch(`/payouts/${r.id}`, { action });
      toast.success(`Payout ${action}d`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const pending = items.filter((r) => r.status === "pending");
  const processed = items.filter((r) => r.status !== "pending");
  return (
    <div className="space-y-6">
      <BrutalCard className="p-0 overflow-hidden">
        <div className="bg-brand-yellow border-b-2 border-black p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-black text-xl">Pending requests ({pending.length})</h3>
            <Chip color="bg-white">Review required</Chip>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white">
            <tr>
              <th className="p-3 text-left">Employee</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Note</th>
              <th className="p-3 text-left">Requested</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-neutral-500">No pending requests</td></tr>
            ) : pending.map((r) => (
              <tr key={r.id} className="border-t-2 border-black/10" data-testid={`payout-row-${r.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{r.user_name}</div>
                  <div className="text-xs text-neutral-600">{r.user_email}</div>
                </td>
                <td className="p-3">
                  <div className="font-display font-black">€{r.amount_points}</div>
                  <div className="text-xs text-neutral-600">{r.amount_points} pts</div>
                </td>
                <td className="p-3 text-xs max-w-xs">{r.note || "—"}</td>
                <td className="p-3 text-xs text-neutral-700">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => act(r, "approve")} className="btn-brutal btn-green text-xs" data-testid={`approve-payout-${r.id}`}>
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => act(r, "deny")} className="btn-brutal btn-white text-xs" data-testid={`deny-payout-${r.id}`}>
                      <X className="w-3 h-3" /> Deny
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </BrutalCard>

      <BrutalCard className="p-0 overflow-hidden">
        <div className="bg-neutral-100 border-b-2 border-black p-4">
          <h3 className="font-display font-black text-xl">History ({processed.length})</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white">
            <tr>
              <th className="p-3 text-left">Employee</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Processed</th>
            </tr>
          </thead>
          <tbody>
            {processed.map((r) => (
              <tr key={r.id} className="border-t-2 border-black/10">
                <td className="p-3">{r.user_name}</td>
                <td className="p-3">€{r.amount_points}</td>
                <td className="p-3">
                  <Chip color={r.status === "approved" ? "bg-brand-green" : "bg-red-200"}>{r.status}</Chip>
                </td>
                <td className="p-3 text-xs text-neutral-700">
                  {r.processed_at ? new Date(r.processed_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </BrutalCard>
    </div>
  );
}

function SpotlightAdmin() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/spotlight");
      setCurrent(data);
    } catch {
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clear = async () => {
    if (!window.confirm("Clear the current month's spotlight? The dashboard card will disappear until someone earns it back through real referrals this month.")) return;
    try {
      await api.delete("/spotlight/current");
      toast.success("Spotlight cleared — will re-earn automatically from referrals");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <BrutalCard className="p-5 lg:col-span-2">
        <h3 className="font-display font-black text-xl mb-4">Current spotlight</h3>
        {loading ? (
          <div className="text-neutral-500 text-sm">Loading…</div>
        ) : current ? (
          <div className="flex items-center gap-4 flex-wrap">
            <img
              src={current.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(current.name)}`}
              alt=""
              className="w-20 h-20 rounded-2xl border-2 border-black object-cover"
              data-testid="current-spotlight-avatar"
            />
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-black text-2xl" data-testid="current-spotlight-name">{current.name}</div>
              <div className="text-sm text-neutral-700">{current.headline}</div>
              <div className="text-xs text-neutral-600 mt-1">
                Auto-selected · {current.points} pts · {current.total_referrals} refs · {current.hired_referrals} hires
              </div>
            </div>
            <button onClick={clear} className="btn-brutal bg-red-500 text-white text-sm" data-testid="clear-spotlight-btn">
              <Trash2 className="w-3.5 h-3.5" /> Clear spotlight
            </button>
          </div>
        ) : (
          <div className="text-neutral-600 text-sm" data-testid="no-spotlight">
            No spotlight this month yet — it's auto-assigned to the top referrer once someone logs real referral activity.
          </div>
        )}
      </BrutalCard>

      <BrutalCard className="p-5 bg-brand-yellow">
        <h3 className="font-display font-black text-xl mb-2">How spotlights work</h3>
        <p className="text-sm mb-3">
          Spotlights are <strong>earned, not picked</strong>. The top monthly referrer (with at least one real referral) is automatically featured on the dashboard and Spotlight page.
        </p>
        <p className="text-sm mb-3">
          Want to celebrate someone for a non-referral win (Carer of the Month, hitting a KPI, a personal milestone)? Use <strong>Shout-outs</strong> instead — those are ad-hoc and fully under your control.
        </p>
        <a href="/app/shoutouts" className="btn-brutal btn-white w-full text-sm justify-center" data-testid="spotlight-to-shoutouts">
          Open Shout-outs <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </BrutalCard>
    </div>
  );
}
